/**
 * Shipment detail drawer (§16) — read-only: order ref, carrier/service, tracking
 * number + carrier link, customs, parcels, tracking events, and label
 * availability (download wires up when carriers create labels — not this
 * increment). Uses the shared Drawer (scrim/Esc/focus-trap).
 */
import { useEffect, useState } from "react";
import { api, type ShipmentDetail } from "../../lib/api.js";
import { humanizeServiceCode, humanizeCode, storeName, formatMoney } from "../../lib/format.js";
import { formatAge } from "../../lib/age.js";
import { Drawer, Badge, Tag, Skeleton, ErrorState, Icon, CarrierMark } from "../../components/ui/index.js";

export function ShipmentDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<ShipmentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    api
      .getShipment(id)
      .then((d) => !cancelled && setDetail(d))
      .catch(() => !cancelled && setError("Couldn’t load this shipment."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const s = detail?.shipment;
  return (
    <Drawer
      onClose={onClose}
      ariaLabel={s ? `Shipment for ${s.orderName}` : "Shipment detail"}
      title={s ? <h1 className="mono">{s.orderName}</h1> : <h1 className="mono">Shipment</h1>}
      meta={
        s ? (
          <div className="od-meta">
            <span>{storeName(s)}</span>
            <span className="od-meta__sep">·</span>
            <CarrierMark code={s.carrierCode} showName />
            <span className="cell-sub">{humanizeServiceCode(s.serviceCode)}</span>
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <div className="skeleton-stack">
          <Skeleton width="60%" height="var(--sp-4)" />
          <Skeleton width="90%" height="var(--sp-16)" />
        </div>
      ) : error ? (
        <ErrorState title="Couldn’t load this shipment" />
      ) : !s ? (
        <p className="muted">Shipment not found.</p>
      ) : (
        <>
          <section className="od-section">
            <h2 className="od-section__title eyebrow">Status</h2>
            <div className="kv">
              <Badge>{humanizeCode(s.status)}</Badge>
              <span className="od-meta__sep">·</span>
              <span className="kv__k">Parcel</span>
              <span className="tnum">
                {s.parcelIndex} / {s.parcelCount}
              </span>
              <span className="od-meta__sep">·</span>
              <span className="kv__k">Created</span>
              <span title={s.createdAt}>{formatAge(s.createdAt)}</span>
            </div>
            {s.errorDetail && <p className="od-exception"><Icon name="alert" size={15} />{s.errorDetail}</p>}
          </section>

          <section className="od-section">
            <h2 className="od-section__title eyebrow">Tracking</h2>
            {s.trackingNumber ? (
              <div className="kv">
                <span className="cell-mono">{s.trackingNumber}</span>
                {s.trackingUrl && (
                  <a href={s.trackingUrl} target="_blank" rel="noreferrer noopener" className="kv">
                    Track <Icon name="external" size={13} />
                  </a>
                )}
              </div>
            ) : (
              <p className="muted">No tracking number yet.</p>
            )}
            {detail.trackingEvents.length > 0 && (
              <ul className="readiness-list" style={{ marginTop: "var(--sp-3)" }}>
                {detail.trackingEvents.map((e, i) => (
                  <li key={i} className="readiness-item">
                    <span className="cell-stack">
                      <span>{e.description}</span>
                      <span className="cell-sub">
                        {humanizeCode(e.statusCode)}
                        {e.location ? ` · ${e.location}` : ""} · {formatAge(e.occurredAt)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="od-section">
            <h2 className="od-section__title eyebrow">Customs & label</h2>
            <div className="cell-stack">
              <div className="kv">
                <span className="kv__k">Incoterm</span>
                <span>{s.incoterm ?? "—"}</span>
                <span className="od-meta__sep">·</span>
                <span className="kv__k">Customs value</span>
                <span className="tnum">
                  {s.customsValueTotal != null ? formatMoney(s.customsValueTotal, s.customsCurrency) : "—"}
                </span>
              </div>
              <div className="kv">
                <span className="kv__k">Label</span>
                {s.labelAvailable ? <Tag>{s.labelFormat ?? "available"}</Tag> : <span className="muted">not created</span>}
              </div>
            </div>
          </section>
        </>
      )}
    </Drawer>
  );
}
